// TenantSettingsScreen — brand settings for the tenant.
//
// v0.42.0 — initial. Three inputs:
//   1. Brand name (NVARCHAR(120)) — shown on the PDF cover wordmark
//   2. Brand color (hex, NVARCHAR(20)) — accent strip + heading hue
//   3. Logo upload — file -> data URL on Tenants.BrandLogoUrl. The
//      PDF cover renders this in place of the drawn pin glyph when set.
//
// These are persisted to the Tenants table via /api/tenant. The values
// are stored regardless of tier — PDF rendering applies them ONLY on
// Pro tier (see defect-report-template.js tier check), so upgrading
// flips white-label on without re-uploading anything.

import React, { useEffect, useState } from 'react';
import { apiGet, apiSend } from '../api';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
import { isFreeTier } from '../utils/tier';
import UpgradePrompt from '../components/UpgradePrompt';
// 2026-08-14 — the tenant's own pins + inspection tool sets.
import MarkCatalogueEditor from '../components/MarkCatalogueEditor';
// 2026-09-15 — the wording of the email a shared report is sent with.
import ShareEmailTemplateEditor from '../components/ShareEmailTemplateEditor';

const COMMON_COLORS = [
  { hex: '#fb923c', label: 'DropPin orange' },
  { hex: '#0284c7', label: 'Blue' },
  { hex: '#15803d', label: 'Green' },
  { hex: '#dc2626', label: 'Red' },
  { hex: '#7c3aed', label: 'Purple' },
  { hex: '#111827', label: 'Near-black' },
];

export default function TenantSettingsScreen() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [upgradeReason, setUpgradeReason] = useState(null);

  // Local form state — synced from the loaded tenant.
  const [brandName, setBrandName] = useState('');
  const [brandColor, setBrandColor] = useState('');
  const [brandLogoUrl, setBrandLogoUrl] = useState('');

  const free = isFreeTier();

  async function load() {
    setLoading(true); setError(null);
    try {
      const t = await apiGet('/api/tenant');
      setTenant(t);
      setBrandName(t.brandName || '');
      setBrandColor(t.brandColor || '');
      setBrandLogoUrl(t.brandLogoUrl || '');
    } catch (e) {
      const msg = e?.message || String(e);
      // v0.50.0 — 404 "Tenant not found" means the account row hasn't been
      // provisioned yet (can happen on a brand-new account before first login
      // completes server-side setup). Show a clear, actionable message rather
      // than the raw API error.
      if (msg.includes('Tenant not found') || msg.includes('404')) {
        setError('Settings not available yet — your account may still be provisioning. Try refreshing in a moment.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function onLogoFile(file) {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      window.alert('Logo file is over 1MB. Compress it (PNG with transparency works best) and try again.');
      return;
    }
    const url = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(new Error('Could not read the file.'));
      r.readAsDataURL(file);
    });
    setBrandLogoUrl(url);
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const updated = await apiSend('PATCH', '/api/tenant', {
        brandName: brandName.trim() || null,
        brandColor: brandColor.trim() || null,
        brandLogoUrl: brandLogoUrl || null,
      });
      setTenant(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>Loading…</div>;
  }
  if (error && !tenant) {
    return <div style={{ ...card, padding: 24, fontSize: 13, color: theme.danger }}>{error}</div>;
  }

  return (
    <div>
      <PageHeading>Settings</PageHeading>
      <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4, marginBottom: 18 }}>
        Brand the PDF reports your customers receive. {free && (
          <span style={{ color: theme.brand, fontWeight: 600 }}>
            Available on Pro — your edits save now, but the PDF stays DropPin-branded until you upgrade.
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ ...card, padding: 18, maxWidth: 720 }}>
        <Section title="Brand name" hint="Shown on the PDF cover wordmark in place of 'DropPin'.">
          <input
            type="text"
            value={brandName}
            onChange={e => setBrandName(e.target.value)}
            placeholder="e.g. InSite Building Group"
            maxLength={120}
            style={{ ...input, maxWidth: 320 }}
          />
        </Section>

        <Section title="Brand color" hint="Hex code. Used for the accent strip + heading underlines.">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={brandColor}
              onChange={e => setBrandColor(e.target.value)}
              placeholder="#fb923c"
              maxLength={20}
              style={{ ...input, width: 140 }}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {COMMON_COLORS.map(c => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setBrandColor(c.hex)}
                  title={c.label}
                  style={{
                    width: 26, height: 26, borderRadius: '50%',
                    background: c.hex, cursor: 'pointer',
                    border: brandColor.toLowerCase() === c.hex.toLowerCase()
                      ? `2px solid ${theme.text}` : `1px solid ${theme.border}`,
                  }}
                />
              ))}
            </div>
          </div>
        </Section>

        <Section title="Logo" hint="PNG with transparency works best. Drawn in the cover header in place of the pin glyph. Max 1MB.">
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{
              width: 120, height: 80,
              border: `1px dashed ${theme.border}`,
              borderRadius: 8,
              background: '#0f172a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
              padding: 8,
            }}>
              {brandLogoUrl
                ? <img src={brandLogoUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>(no logo)</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={(e) => onLogoFile(e.target.files?.[0])}
                style={{ fontSize: 12 }}
              />
              {brandLogoUrl && (
                <button type="button" onClick={() => setBrandLogoUrl('')}
                  style={{ ...btnSecondary, alignSelf: 'flex-start', fontSize: 12, padding: '4px 10px', color: theme.danger }}>
                  Remove logo
                </button>
              )}
            </div>
          </div>
        </Section>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 18 }}>
          <button type="button" onClick={save} disabled={saving}
            style={{ ...btnPrimary, opacity: saving ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {savedAt && (
            <span style={{ fontSize: 12, color: theme.textLight }}>
              Saved {savedAt.toLocaleTimeString()}.
            </span>
          )}
          {free && (
            <button type="button"
              onClick={() => setUpgradeReason('whitelabel')}
              style={{ ...btnSecondary, marginLeft: 'auto', fontSize: 12, color: theme.brand, fontWeight: 700 }}>
              Get Pro to apply branding
            </button>
          )}
        </div>
      </div>

      {/* 2026-08-14 — mark types. Its own card below the branding block: it is a
          different kind of setting (a vocabulary, not a look) and it will keep
          growing as inspection types are added. */}
      <div style={{ marginTop: 18 }}>
        <MarkCatalogueEditor />
      </div>

      <div style={{ marginTop: 18 }}>
        <ShareEmailTemplateEditor />
      </div>

      {/* v0.268.0 — where a client's reply through a share link is copied. */}
      <div style={{ marginTop: 18 }}>
        <ClientRepliesCard tenant={tenant} onSaved={setTenant} />
      </div>

      <UpgradePrompt
        open={!!upgradeReason}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />
    </div>
  );
}

// v0.268.0 — Brett: "yeh cc service in. but make it optional in settings".
// A client's reply emails whoever sent them the link, once per sitting; this
// address gets a copy (and is the one told about links made before v0.268.0).
// Empty = no copy. The server allows an owner or admin to change it and says
// why when it refuses (not allowed, database not updated, not an address).
function ClientRepliesCard({ tenant, onSaved }) {
  const [cc, setCc] = useState(tenant?.replyCcEmail || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const ready = tenant?.replyCcReady !== false;
  const changed = cc.trim().toLowerCase() !== String(tenant?.replyCcEmail || '').toLowerCase();

  async function saveCc() {
    setBusy(true); setErr(null);
    try {
      const updated = await apiSend('PATCH', '/api/tenant', { replyCcEmail: cc.trim() || null });
      onSaved(updated);
      setCc(updated.replyCcEmail || '');
      setSavedAt(new Date());
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...card, padding: 18, maxWidth: 720 }} data-testid="client-replies">
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 12 }}>Client replies</div>
      <Section title="Copy reply emails to"
        hint="When a client replies on a shared report, the person who sent them the link is emailed once per sitting, with a button that opens the item. This address gets a copy, and is the one told about links sent before this version. Leave it empty for no copy.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            type="email"
            value={cc}
            onChange={e => { setCc(e.target.value); setSavedAt(null); }}
            placeholder="e.g. service@yourcompany.com"
            maxLength={254}
            disabled={!ready}
            aria-label="Copy reply emails to"
            style={{ ...input, maxWidth: 320 }}
          />
          <button type="button" onClick={saveCc} disabled={busy || !ready || !changed}
            style={{ ...btnPrimary, opacity: busy || !ready || !changed ? 0.5 : 1 }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {savedAt && !changed && (
            <span style={{ fontSize: 12, color: theme.textLight }}>
              {cc ? 'Saved' : 'No copy'} · {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        {!ready && (
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: 8 }} data-testid="client-replies-not-ready">
            Needs a database update before it can be set. Reply emails still go to whoever sent the link.
          </div>
        )}
        {err && (
          <div style={{ fontSize: 12, color: theme.danger, marginTop: 8 }} role="alert">{err}</div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{title}</div>
      {hint && <div style={{ fontSize: 11, color: theme.textLight, marginTop: 2, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}
